import { Component, Input, OnInit } from '@angular/core';
import { QuoteLeadUserTitleEnum, TellUsAboutYouSectionFormPayload } from '@lx/quote-tool/shared/types/quote';
import { FormFieldObject, FormType } from '@lx/quote-tool/shared/types';
import { CodeAndName } from '@lx/shared/types';
import { getDateStringFromDate, getToday } from '@lx/shared/utils';
import { emailValidator } from '@lx/core/validators/email.validator';
import { StepService } from '@lx/quote-tool/shared/services/step-service';
import { ConfigurationService } from '@lx/shared/app-config';
import { FormSection } from '@lx/quote-tool/shared/ui/directives';
import { DEFAULT_ERROR_MESSAGE } from '@lx/shared/errors';
import { phoneValidator } from '@lx/quote-tool/shared/validators';

@Component({
  selector: 'lx-tell-us-about-you-section',
  templateUrl: './tell-us-about-you.component.html',
  styleUrls: ['./tell-us-about-you.component.scss'],
})
export class TellUsAboutYouSectionComponent extends FormSection<TellUsAboutYouSectionFormPayload> implements OnInit {
  @Input() formType: FormType;
  @Input() validationRequests = 0;

  // Flag to indicate if form have validation errors and if true, show the error message
  @Input() withValidationErrors: boolean;

  calendarMaxDate = getToday();
  titleOptions: CodeAndName[] = [
    { code: QuoteLeadUserTitleEnum.MR, name: $localize`Mr.` },
    { code: QuoteLeadUserTitleEnum.MS, name: $localize`Ms.` },
  ];
  identityDocumentFieldIsEnabled = false;

  constructor(stepService: StepService) {
    super(stepService);
  }

  ngOnInit(): void {
    this.identityDocumentFieldIsEnabled = ConfigurationService.getValue('ENROLLMENT_IDENTITY_DOCUMENT_ENABLED') as boolean;
    super.ngOnInit();
  }

  onValueChanges(key: keyof TellUsAboutYouSectionFormPayload): void {
    this.formData[key].validate();
    this.storeStudentDetails();
  }

  onSelectChanged(key: keyof TellUsAboutYouSectionFormPayload, selectedItem: string) {
    this.formData[key].value = selectedItem;
    this.onValueChanges(key);
  }

  onSelectBirthDate(date: Date): void {
    this.formData.dateOfBirth.value = getDateStringFromDate(date);
    this.onValueChanges('dateOfBirth');
  }

  updatePhoneNumber($event: string, key: keyof TellUsAboutYouSectionFormPayload) {
    this.formData.phoneNumber.value = $event ? $event : undefined;
    this.onValueChanges(key);
  }

  splitPhoneDigits(value: string, direction: number) {
    return value?.split(' ')[direction];
  }

  isEnrolmentForm(): boolean {
    return this.formType === FormType.ENROLLMENT;
  }

  isPriceRequestForm(): boolean {
    return this.formType === FormType.PRICE_REQUEST;
  }

  recalculateFormValidation(key: keyof TellUsAboutYouSectionFormPayload) {
    this.onValueChanges(key);
    Object.entries(this.formData).forEach(([_key, formField]) => {
      formField.recalculateRequired();
      if (formField.value) {
        formField.validate();
      }
    });
  }

  onAutoFill(key: keyof TellUsAboutYouSectionFormPayload): void {
    this.recalculateFormValidation(key);
  }

  protected getFormFields(): { [K in keyof Partial<TellUsAboutYouSectionFormPayload>]: FormFieldObject<TellUsAboutYouSectionFormPayload[K]> } {
    const commonFields: { [K in keyof Partial<TellUsAboutYouSectionFormPayload>]: FormFieldObject<TellUsAboutYouSectionFormPayload[K]> } = {
      title: {
        value: null,
        validator: (title) => this.titleOptions?.findIndex(({ code }) => code === title) >= 0,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      firstName: {
        value: '',
        validator: (firstName) => firstName && firstName.length <= 80,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      lastName: {
        value: '',
        validator: (lastName) => lastName && lastName.length <= 40,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      email: {
        value: '',
        validator: (email) => emailValidator(email),
        errorMessage: null,
        required: true,
      },
      phoneNumber: {
        value: '',
        validator: (phone) => phone && phoneValidator(phone) && this.splitPhoneDigits(phone, 1).length > 4 && /\d/.test(phone),
        errorMessage: null,
        required: true,
      },
      postcode: {
        value: '',
        validator: (postcode) => postcode && postcode.length > 3 && postcode.length <= 20,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      city: {
        value: '',
        validator: (city) => city && city.length <= 40,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      countryOfResidence: {
        value: this.getCountryOfResidence(),
        validator: Boolean,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
    };
    const enrollmentFormsFields = {
      identityDocument: {
        value: '',
        validator: (identityDocument: string) => identityDocument && identityDocument.length > 0,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: this.identityDocumentFieldIsEnabled,
      },
      address: {
        value: '',
        validator: (address: string) => address && address.length < 80,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      placeOfBirth: {
        value: '',
        validator: (birthPlace: string) => birthPlace && birthPlace.length < 80,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      motherTongue: {
        value: '',
        validator: (motherTongue: string) => !!motherTongue,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
    };
    const dateOfBirthFormField = {
      dateOfBirth: {
        value: null,
        validator: (birthDate: string) => new Date(birthDate).getTime() < this.calendarMaxDate.getTime(),
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
    };
    const nationalityFormField = {
      nationality: {
        value: '',
        validator: (nationality: string) => !!nationality,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
    };
    return {
      ...commonFields,
      ...(this.isEnrolmentForm() || this.isPriceRequestForm() ? dateOfBirthFormField : {}),
      ...(this.isEnrolmentForm() || this.isPriceRequestForm() ? nationalityFormField : {}),
      ...(this.isEnrolmentForm() ? enrollmentFormsFields : {}),
    };
  }

  protected getFieldsValues(fieldKeys: Array<keyof TellUsAboutYouSectionFormPayload>): Partial<TellUsAboutYouSectionFormPayload> {
    const values: Partial<TellUsAboutYouSectionFormPayload> = {};

    fieldKeys.forEach((key: keyof TellUsAboutYouSectionFormPayload) => {
      if (this.getFieldValueIfValid(key) !== undefined || this.getFormFields()[key].required) {
        values[key] = this.getFieldValueIfValid(key);
      }
    });

    return values;
  }

  private getCountryOfResidence(): TellUsAboutYouSectionFormPayload['countryOfResidence'] {
    return this.stepService.getResidenceData()?.countryOfResidence ?? '';
  }
}
